export interface ApiResponse {
  success: true;
  message: string;
}

export interface GenericErrorResponse {
  success: false;
  error?: string;
  warning?: string;
  [key: string]: unknown;
}

export interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
  // Public endpoints opt out so a 401 can't end the caller's session.
  skipAuthLogout?: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

class ApiRequestError extends Error {
  details: GenericErrorResponse;

  constructor(details: GenericErrorResponse) {
    super(details.error || "Request failed");
    this.name = "ApiRequestError";
    this.details = details;
  }
}

export const buildApiUrl = (endpoint: string): string => {
  return endpoint.startsWith("http") ? endpoint : `${API_BASE_URL}${endpoint}`;
};

const buildRequestConfig = (options: FetchOptions = {}): RequestInit => {
  const { skipAuthLogout: _skipAuthLogout, ...init } = options;

  return {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...init,
  };
};

const toNetworkError = (error: unknown): GenericErrorResponse => ({
  success: false,
  error:
    error instanceof Error
      ? error.message
      : "Network error. Please check your connection.",
});

const handleApiError = async (
  response: Response,
  options: FetchOptions = {}
): Promise<GenericErrorResponse> => {
  const errorData = await response.json().catch(() => ({}));
  const errorMessage =
    errorData.error || `HTTP error! status: ${response.status}`;

  if (response.status === 401 && !options.skipAuthLogout) {
    window.dispatchEvent(new CustomEvent("auth:logout"));
  }

  return { success: false, error: errorMessage, ...errorData };
};

const request = async (
  endpoint: string,
  options: FetchOptions = {}
): Promise<Response> => {
  try {
    const response = await fetch(buildApiUrl(endpoint), buildRequestConfig(options));

    if (!response.ok) {
      throw new ApiRequestError(await handleApiError(response, options));
    }

    return response;
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error;
    }

    console.error(`API call failed: ${endpoint}`, error);
    throw new ApiRequestError(toNetworkError(error));
  }
};

const parseJson = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
};

export const apiJson = async <T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> => {
  const response = await request(endpoint, options);
  return parseJson<T>(response);
};

export const apiBlob = async (
  endpoint: string,
  options: FetchOptions = {}
): Promise<Blob> => {
  const response = await request(endpoint, options);
  return response.blob();
};

export const apiRequest = async <T extends ApiResponse = ApiResponse>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T | GenericErrorResponse> => {
  try {
    const response = await request(endpoint, options);
    const data = await parseJson<unknown>(response);
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return { success: true, ...data } as T;
    }

    return { success: true, data } as unknown as T;
  } catch (error) {
    return error instanceof ApiRequestError ? error.details : toNetworkError(error);
  }
};

export const requireSuccess = <T extends ApiResponse>(
  response: T | GenericErrorResponse,
  fallbackMessage: string
): T => {
  if (!response.success) {
    throw new Error(response.error || fallbackMessage);
  }

  return response;
};